import React, { type PropsWithChildren, useCallback } from 'react';
import {
  type GestureResponderEvent,
  StyleSheet,
  TouchableOpacity,
  type TouchableOpacityProps,
  View,
  type ViewProps,
} from 'react-native';

import { theme } from '@garnish/constants';
import { Icon } from '@garnish/core';
import { BodyText } from '@garnish/core';
import { HapticFeedbackStyle, triggerHapticFeedback } from '@garnish/haptics';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeaderLocationButton = (props: Props) => {
  const { children, disabled, onPress, style, ...restProps } = props;

  // ─── Handlers ───────────────────────────────────────────────────────

  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      void triggerHapticFeedback(HapticFeedbackStyle.Light);
      onPress?.(event);
    },
    [onPress],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const buttonStyles = [styles.button, disabled ? styles.disabledButton : undefined, style];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole="button"
      activeOpacity={0.8}
      disabled={disabled}
      onPress={handlePress}
      style={buttonStyles}
      {...restProps}
    >
      <BodyText
        maxFontSizeMultiplier={1.2}
        numberOfLines={1}
        sizeMatch={['16']}
        style={styles.title}
      >
        {children}
      </BodyText>

      <View style={styles.iconContainer}>
        <Icon color={theme.colors.WHITE} name="IconCaretDown" width={14} height={14} />
      </View>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  disabledButton: {
    opacity: 0.5,
  },
  iconContainer: {
    flexShrink: 0,
  },
  title: {
    flexShrink: 1,
    textDecorationLine: 'underline',
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<
  {
    color?: string;
    disabled?: boolean;
    style?: ViewProps['style'];
  } & Omit<TouchableOpacityProps, 'children' | 'disabled' | 'style'>
>;
